// Live cockpit (RESTYLE §3, DESIGN_SPEC §11.4.12; dispatched → collecting): the report while the guest runs.
//   <LiveCockpit ctx facts />
//   Above the fold: the header bar, one instrument row: kicker, the mono sample name (two lines at most), LIVE (+ "overdue
//   +1:52" past the timeout, lib/records liveness), "verdict at collection" and the stream line on the left; the run clock
//   against the timeout with a live progress bar and events per second over the last 60 s, then Save frame on the right.
//   Its height never depends on the phase or on a failed action (its note takes the clock's line). Then
//   FIG 01 the guest screen (≈62%, track F's ScreenPlate) beside FIG 02 the live process tree and its counters (≈38%).
//   Below: FIG 03 third-party analysis (the lookups start with the run; each service reads "checking" until it answers;
//   absent while the backend has no intel), FIG 04 the live detail tabs with counts (Events · Network · Indicators so
//   far · Observed so far; the tab row sticks under the nav with the clock), FIG 05 Run (lifecycle, identity, child
//   analyses), FIG 06 Still filling. Nothing malbox scores is shown live. Keys: j / k next / previous figure, 1–6 jump.
import { useLayoutEffect, useMemo, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import {
  CopyButton,
  FigHead,
  Kicker,
  Kid,
  LiveBadge,
  LiveLine,
  ProgressBar,
  Readouts,
  Sep,
  Sparkline,
  Tabs,
  VerdictBanner,
  tabPanelProps,
  type ReadoutItem,
  type TabItem,
} from "@/components/ui";
import { useClock, useHotkeys } from "@/hooks";
import { agoFine, clock, count, plural, secs, utc } from "@/lib/format";
import type { JobStream, ReportCtx } from "@/lib/derive/types";
import { jobName, liveness } from "@/lib/records";
import { ChildJobs, ParentLink } from "./ChildJobs";
import { runClock, type RunClock } from "./cockpit";
import { treeOf } from "./derive/tree";
import type { ReportFacts } from "./facts";
import { IdentitySheets } from "./IdentitySheets";
import { displayOf } from "./lifecycle";
import { observed } from "./observed";
import { ObservedList } from "./ObservedSoFar";
import { IndicatorsLive } from "./panels/IndicatorsFig";
import { NetworkLive } from "./panels/NetworkFig";
import { ProcessTreeLive } from "./panels/ProcessTreeFig";
import { ThirdPartySection } from "./panels/ThirdPartyFig";
import { ScreenPlate } from "./panels/ScreenPlate";
import { StreamLive } from "./panels/StreamLive";
import { PanelBoundary, FigRow } from "./ReportBody";
import { Lifecycle, scrollToFig } from "./ReportHeader";
import { StillFilling, liveIocCount } from "./StillFilling";
import { stepFrom, useScrollSpy } from "./useScrollSpy";

type CtxProps = { ctx: ReportCtx };

const FIGS = [1, 2, 3, 4, 5, 6] as const;
const TAB_IDS = ["events", "network", "indicators", "observed"] as const;
type TabId = (typeof TAB_IDS)[number];
export const LIVE_TABS = "live";

/** A headline longer than this gets a copy button (it is clamped to two lines). */
const LONG_NAME = 48;

// ---- header bar -----------------------------------------------------------------------------------

function StreamStatus({ stream, now }: { stream: JobStream; now: number }) {
  const last = stream.rate.lastEventAt;
  const seq = stream.lastSeq;
  if (stream.connection === "reconnecting" || stream.connection === "closed") {
    const retryIn = stream.retry ? Math.max(0, stream.retry.at - now) : null;
    return (
      <LiveLine state="lost" className="mt-2">
        <b>Reconnecting…</b>
        {last !== null && <> · last event {agoFine(last, Math.max(now, last))}</>} · resumes from seq {count(seq + 1)}
        {retryIn !== null && <> · retrying in {secs(retryIn + 999)}</>} ·{" "}
        <button type="button" className="lnk" onClick={stream.reconnect}>
          Retry now
        </button>
      </LiveLine>
    );
  }
  if (seq === 0)
    return (
      <p className="meta mt-2">
        <span className="breathe">{stream.connection === "connecting" ? "connecting to the event stream" : "waiting for the first event"}</span>
      </p>
    );
  const idle = last !== null && now - last > 10_000;
  return (
    <p className="meta mt-2">
      seq {count(seq)} · {idle && last !== null ? `idle ${secs(now - last)}` : last !== null ? `last event ${agoFine(last, Math.max(now, last))}` : "streaming"}
    </p>
  );
}

/** The run clock, re-read every second (only the components that show it tick). */
function useRunClock(ctx: ReportCtx, facts: ReportFacts): { rc: RunClock; now: number } {
  const now = useClock(1000);
  const { job, stream } = ctx;
  const rc = runClock({
    now,
    t0: stream.t0,
    t0Source: stream.t0Source,
    phase: facts.phaseDisplay,
    since: facts.since,
    transitions: stream.transitions,
    timeoutMs: facts.timeoutMs,
    dispatchedAt: job.dispatched_at,
    submittedAt: job.submitted_at,
  });
  return { rc, now };
}

const clockShort = (rc: RunClock) => (rc.running && rc.timeoutMs > 0 ? `${clock(rc.elapsedMs)} / ${clock(rc.timeoutMs)}` : `${rc.phase} ${clock(rc.elapsedMs)}`);

function clockLine(rc: RunClock, t0: number | null, seq: number): string {
  if (rc.collectingMs !== null) return `collecting for ${clock(rc.collectingMs)} · the guest ran ${clock(rc.elapsedMs)}`;
  if (!rc.running) {
    // no running job.state on the stream: the phase is the record's, which says nothing about the sample
    if (rc.phase === "running" || rc.phase === "collecting") return seq === 0 ? "no events yet · state from the record" : `the record says ${rc.phase} · T0 not on the stream yet`;
    return `${rc.phase === "provisioning" ? "provisioning the guest" : rc.phase === "booting" ? "the guest is booting" : rc.phase} · the sample has not started`;
  }
  if (rc.overMs > 0) return `past the ${clock(rc.timeoutMs)} timeout by ${clock(rc.overMs)} · collection has not started`;
  if (t0 === null) return "";
  return rc.timeoutMs > 0 ? `T0 ${utc(t0)} UTC · collection by ${utc(t0 + rc.timeoutMs)}` : `T0 ${utc(t0)} UTC · no timeout set`;
}

function RunClockBlock({
  rc,
  series,
  perSec,
  t0,
  seq,
  className,
}: {
  rc: RunClock;
  series: number[];
  perSec: number;
  t0: number | null;
  seq: number;
  className?: string;
}) {
  const peak = Math.max(0, ...series);
  const text = rc.running && rc.timeoutMs > 0 ? `${clock(rc.elapsedMs)} of a ${clock(rc.timeoutMs)} timeout` : rc.running ? clock(rc.elapsedMs) : `${rc.phase}, ${clock(rc.elapsedMs)}`;
  return (
    <div className={`grid min-w-0 content-start gap-2 ${className ?? ""}`} role="group" aria-label={`Run clock: ${text}`}>
      {/* one row whatever the phase: the phase word lives in the line below, never beside the numeral */}
      <div className="flex flex-wrap items-end gap-x-3 gap-y-1 min-[1280px]:flex-nowrap">
        <span className="inline-flex items-end gap-2 whitespace-nowrap" aria-hidden="true">
          <span className="numeral text-[clamp(38px,3.2vw,48px)] leading-[.82]">{clock(rc.elapsedMs)}</span>
          {rc.running && rc.timeoutMs > 0 && <span className="font-mono text-[13px] leading-none text-ink-mute">/ {clock(rc.timeoutMs)}</span>}
        </span>
        <span className="ml-auto inline-flex items-center gap-2 whitespace-nowrap">
          <Sparkline values={series} width={88} height={24} label={`Events per second over the last 60 s: ${series[series.length - 1] ?? 0} in the last second, peak ${peak}`} />
          <span className="font-mono text-meta text-ink-mute">
            <b className="font-medium text-ink tabular-nums">{perSec.toFixed(1)}</b> ev/s
          </span>
        </span>
      </div>
      <ProgressBar value={rc.running ? rc.elapsedMs : 0} max={rc.timeoutMs || 1} tone="live" label="Run clock" valueText={text} />
      <p className="meta">{clockLine(rc, t0, seq)}</p>
    </div>
  );
}

function CockpitHeader({ ctx, facts }: { ctx: ReportCtx; facts: ReportFacts }) {
  const { job, stream } = ctx;
  const { rc, now } = useRunClock(ctx, facts);
  // events per second over the last 60 s, from the store's per-second histogram (O(60) per tick, not O(events))
  const series = stream.rate.series(now, 60);
  const phase = displayOf(stream.state) ?? stream.state;
  const name = jobName(job);
  const overdue = liveness(stream.state, job, now) === "overdue";
  return (
    <header
      id="top"
      className="grid items-start gap-x-8 gap-y-4 border-b border-rule-cell pb-5 min-[900px]:grid-cols-[minmax(0,1fr)_380px] min-[1280px]:grid-cols-[minmax(0,1fr)_auto]"
    >
      <div className="min-w-0">
        <Kicker dash>
          Analysis <Kid>{job.id}</Kid>
          <Sep />
          <span>{phase}</span>
          {stream.recordLags && (
            <>
              <Sep />
              <span className="normal-case tracking-[.04em] text-ink-faint">record syncing</span>
            </>
          )}
        </Kicker>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
          <h1 className="headline line-clamp-2 min-w-0 text-[clamp(22px,2vw,30px)]" translate="no" title={name}>
            {name}
          </h1>
          {name.length > LONG_NAME && <CopyButton value={name} label="sample name" />}
          <span className="inline-flex flex-wrap items-center gap-2">
            {overdue ? (
              <LiveBadge size="lg" ping={false} title="On a node past its timeout: the record has not caught up, or the node has not collected">
                overdue{rc.overMs > 0 ? ` +${clock(rc.overMs)}` : ""}
              </LiveBadge>
            ) : (
              <LiveBadge size="lg" ping={stream.connection !== "reconnecting"} title="The guest is running; events arrive as they happen" />
            )}
            <VerdictBanner v="pending" size="sm" label="verdict at collection" title="Nothing is scored while the run is live" />
          </span>
        </div>
        {job.parent_job_id && <ParentLink parentId={job.parent_job_id} />}
        <StreamStatus stream={stream} now={now} />
      </div>
      <div className="flex min-w-0 flex-wrap items-start gap-x-6 gap-y-4 min-[900px]:justify-end">
        <RunClockBlock
          rc={rc}
          series={series}
          perSec={stream.rate.perSec}
          t0={stream.t0}
          seq={stream.lastSeq}
          className="order-1 w-full min-[900px]:max-[1279px]:order-2 min-[1280px]:w-auto min-[1280px]:max-w-[440px] min-[1280px]:min-w-[340px]"
        />
      </div>
    </header>
  );
}


// ---- cockpit: screen + processes ------------------------------------------------------------

/** The widest 16:10 plate whose frame and caption still end inside the viewport (never under 480px). */
function useFitWidth(below = 60): [(el: HTMLDivElement | null) => void, number | undefined] {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number>();
  useLayoutEffect(() => {
    if (!el) return;
    const measure = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      const pad = Math.min(36, Math.max(22, window.innerWidth * 0.023));
      const room = window.innerHeight - top - below;
      setWidth(Math.max(480, Math.round((room - 2 * pad - 2) * 1.6 + 2 * pad + 2)));
    };
    measure();
    const head = document.getElementById("top");
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    if (head) ro?.observe(head);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [el, below]);
  return [setEl, width];
}

function ScreenCell({ ctx, fit }: CtxProps & { fit: (el: HTMLDivElement | null) => void }) {
  return (
    <section className="min-w-0" aria-labelledby="fig-1-t">
      <FigHead no={1} label="Guest screen" count="not available" />
      <div ref={fit} className="mt-4 w-full">
        <PanelBoundary label="Guest screen" no={1} inline>
          <ScreenPlate jobId={ctx.job.id} live={ctx.stream.live} allowInput={ctx.job.allow_input} saveButton={false} />
        </PanelBoundary>
      </div>
    </section>
  );
}

const num = (n: number) => (n < 1000 ? n : count(n));

function ProcessCell({ ctx, facts }: { ctx: ReportCtx; facts: ReportFacts }) {
  const { stream } = ctx;
  const tree = treeOf(ctx);
  const alive = [...tree.nodes.values()].filter((n) => n.running).length;
  const snap = stream.snap;
  const { dumps, requests } = useMemo(() => {
    let d = 0;
    let r = 0;
    for (const e of snap.events) {
      if (e.kind === "dump.taken") d++;
      else if (e.kind === "net.http" && !ctx.noise?.isBackground(e)) r++;
    }
    return { dumps: d, requests: r };
  }, [snap, ctx.noise]);
  const items: ReadoutItem[] = [
    { label: "events", value: num(snap.events.length) },
    { label: "processes", value: tree.inScope, mark: "process" },
    { label: "hosts", value: facts.hostCount, mark: "network" },
    { label: "requests", value: num(requests), mark: "network" },
    { label: "injected", value: facts.injectedInto.length, mark: "memory" },
    { label: "dumps", value: dumps, mark: "detection" },
  ];
  const inj = tree.injections.length;
  return (
    <section
      className="flex min-w-0 flex-col min-[900px]:min-h-[420px] min-[900px]:border-l min-[900px]:border-rule-cell min-[900px]:pl-cell min-[900px]:[contain:size]"
      aria-labelledby="fig-2-t"
    >
      <FigHead no={2} label="Processes" count={`${count(tree.inScope)} traced${alive ? ` · ${alive} running` : ""}${inj ? ` · ${inj} ${plural(inj, "injection")}` : ""}`} />
      <Readouts className="mt-3 grid-cols-3 border-t-0 [&_.readout]:pr-2.5" label="Live counters" items={items} />
      <ProcessTreeLive ctx={ctx} className="mt-4 min-h-0 flex-1 max-[899px]:max-h-[min(60svh,440px)]" />
    </section>
  );
}

// ---- live detail tabs ---------------------------------------------------------------------------

function TabClock({ ctx, facts }: { ctx: ReportCtx; facts: ReportFacts }) {
  const { rc } = useRunClock(ctx, facts);
  return (
    <LiveBadge size="sm" ping={false}>
      {clockShort(rc)}
    </LiveBadge>
  );
}

function LiveDetail({ ctx, facts, tab, onTab }: { ctx: ReportCtx; facts: ReportFacts; tab: TabId; onTab: (t: TabId) => void }) {
  const snap = ctx.stream.snap;
  const obs = useMemo(() => observed(snap.events, { noise: ctx.noise, max: 8 }), [snap, ctx.noise]);
  const iocs = liveIocCount(ctx);
  const n = snap.events.length;
  const tabs: TabItem<TabId>[] = [
    { id: "events", label: "Events", count: count(n) },
    { id: "network", label: "Network", count: count(facts.hostCount) },
    { id: "indicators", label: "Indicators so far", count: count(iocs) },
    { id: "observed", label: "Observed so far", count: count(obs.total) },
  ];
  const label = tabs.find((t) => t.id === tab)?.label as string;
  let body: ReactNode;
  if (tab === "events") body = <StreamLive ctx={ctx} />;
  else if (tab === "network") body = <NetworkLive ctx={ctx} />;
  else if (tab === "indicators") body = <IndicatorsLive ctx={ctx} />;
  else body = <ObservedList ctx={ctx} rows={obs.rows} total={obs.total} onSeeAll={() => onTab("events")} />;
  return (
    <section className="mt-12 min-w-0" aria-labelledby="fig-4-t">
      <FigHead no={4} label="Live detail" />
      <div className="z-30 mt-3 flex items-stretch bg-paper min-[720px]:sticky min-[720px]:top-nav">
        <Tabs label="Live detail" idBase={LIVE_TABS} tabs={tabs} value={tab} onChange={onTab} className="min-w-0 flex-1" />
        <div className="flex items-center border-b border-rule pl-4 max-[899px]:hidden" aria-hidden="true">
          <TabClock ctx={ctx} facts={facts} />
        </div>
      </div>
      <div {...tabPanelProps(LIVE_TABS, tab)} className="min-w-0 pt-5 [--stream-h:min(66vh,620px)] min-[900px]:min-h-[360px]">
        <PanelBoundary key={tab} label={label} no={4} inline>
          {body}
        </PanelBoundary>
      </div>
    </section>
  );
}

// ---- the page body ------------------------------------------------------------------------------

export function LiveCockpit({ ctx, facts }: { ctx: ReportCtx; facts: ReportFacts }) {
  const { job } = ctx;
  const [tab, setTab] = useState<TabId>("events");

  // figures side by side share a top: the one the reader jumped to wins that tie (as in the figure index)
  // the screen column is 62% of the grid width; the process-tree column takes the rest. The old
  // viewport-height-based clamp was collapsing the plate to its 480px min on normal desktops and
  // the live-view canvas ended up tiny with huge black letterboxing — bad UX for the main view.
  const [fit] = useFitWidth(job.allow_input ? 80 : 60);
  const screenW: number | undefined = undefined;
  const ids = FIGS.map((n) => `fig-${n}`);
  const [picked, setPicked] = useState<string | null>(null);
  const current = useScrollSpy(ids, { prefer: picked });
  const go = (id: string | undefined) => {
    if (!id) return;
    setPicked(id);
    scrollToFig(Number(id.slice(4)));
  };
  useHotkeys({ j: () => go(stepFrom(ids, current, 1)), k: () => go(stepFrom(ids, current, -1)), ...Object.fromEntries(ids.map((id, i) => [String(i + 1), () => go(id)])) });

  // an in-page link to a tab (#live-tab-network) selects it and scrolls to FIG 04
  const onClickCapture = (e: MouseEvent<HTMLDivElement>) => {
    const href = (e.target as HTMLElement).closest?.("a")?.getAttribute("href") ?? "";
    const prefix = `#${LIVE_TABS}-tab-`;
    const id = href.startsWith(prefix) ? href.slice(prefix.length) : "";
    if (!(TAB_IDS as readonly string[]).includes(id)) return;
    e.preventDefault();
    e.stopPropagation();
    setTab(id as TabId);
    scrollToFig(4);
  };

  return (
    <div className="page pt-7" onClickCapture={onClickCapture}>
      <CockpitHeader ctx={ctx} facts={facts} />
      <div
        className="mt-6 grid gap-x-cell gap-y-10 min-[900px]:grid-cols-[minmax(0,min(var(--screen-w),62%))_minmax(0,1fr)]"
        style={{ "--screen-w": screenW ? `${screenW}px` : "62%" } as CSSProperties}
      >
        <ScreenCell ctx={ctx} fit={fit} />
        <ProcessCell ctx={ctx} facts={facts} />
      </div>
      {job.sample_sha && <ThirdPartySection job={job} no={3} hideOff className="mt-12 min-w-0" />}
      <LiveDetail ctx={ctx} facts={facts} tab={tab} onTab={setTab} />
      <div className="mt-12">
        <FigRow>
          <section className="cell s8 fig" aria-labelledby="fig-5-t">
            <FigHead no={5} label="Run" count={[job.profile, job.network].filter(Boolean).join(" · ")} />
            <Lifecycle ctx={ctx} facts={facts} className="mt-6" />
            <div className="mt-8">
              <IdentitySheets job={job} staticReport={ctx.staticReport} />
            </div>
            <ChildJobs job={job} />
          </section>
          <StillFilling ctx={ctx} no={6} />
        </FigRow>
      </div>
    </div>
  );
}
